import { createComponentStore } from '../../utils/createComponentStore';

export const DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY = 'chatSessionId';
export const DEFAULT_AGENT_SESSION_URL_OUTPUT_PLACEHOLDER_KEY = 'agentSessionUrl';

const generateUniqueKey = (prefix) =>
  prefix + '_' + Math.floor(Math.random() * 90000000 + 10000000);

// Each entry is a system-controlled placeholder the AI Agent always emits.
// `systemOutput` is the canonical engine-side discriminator (engine reads
// off this string to find the renamed key); the visible `key` is what the
// author edits in the drawer. New system entries can be added here without
// touching the iteration logic below.
const SYSTEM_OUTPUT_TEMPLATES = [
  {
    systemOutput: DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY,
    type: 'text',
    desc: 'Chat session ID created or used by this run.',
    label: 'Chat Session ID',
    keyPrefix: 'chatSessionId',
  },
  {
    systemOutput: DEFAULT_AGENT_SESSION_URL_OUTPUT_PLACEHOLDER_KEY,
    type: 'text',
    desc: 'Direct link to the agent session — wired through the "On creation" port.',
    label: 'Agent Session URL',
    keyPrefix: 'agentSessionUrl',
  },
];

const buildSystemEntry = (template, existing) => {
  const trimmedKey = String(existing?.key || '').trim();
  // Treat the bare default name as "never customised" so we still
  // auto-uniquify for anyone who hand-edited their JSON to the canonical
  // value and saved.
  const isBareDefault = trimmedKey === template.systemOutput;
  const resolvedKey =
    trimmedKey && !isBareDefault ? trimmedKey : generateUniqueKey(template.keyPrefix);
  return {
    type: template.type,
    desc: template.desc,
    label: template.label,
    ...(existing || {}),
    key: resolvedKey,
    systemOutput: template.systemOutput,
  };
};

const getDefaultOutputPlaceholders = () =>
  SYSTEM_OUTPUT_TEMPLATES.map((template) => buildSystemEntry(template, null));

const isSystemOutputPlaceholder = (placeholder) => {
  const systemOutput = String(placeholder?.systemOutput || '').trim();
  const key = String(placeholder?.key || '').trim();
  return SYSTEM_OUTPUT_TEMPLATES.some(
    (template) =>
      systemOutput === template.systemOutput || key === template.systemOutput
  );
};

// Kept for consumers that imported the original name. Identical behaviour:
// any author-renameable system entry returns true so the UI hides delete.
const isDefaultOutputPlaceholder = isSystemOutputPlaceholder;

const ensureDefaultOutputPlaceholders = (placeholders) => {
  const normalized = Array.isArray(placeholders)
    ? placeholders.filter(Boolean).map((placeholder) => ({ ...placeholder }))
    : [];

  // Pick out the existing system entries in the same order as
  // SYSTEM_OUTPUT_TEMPLATES, preserving any rename, and drop them from the
  // user-authored tail.
  const systemEntries = [];
  const consumed = new Set();
  for (const template of SYSTEM_OUTPUT_TEMPLATES) {
    const existing =
      normalized.find(
        (placeholder) =>
          !consumed.has(placeholder)
          && String(placeholder?.systemOutput || '').trim() === template.systemOutput
      ) ||
      normalized.find(
        (placeholder) =>
          !consumed.has(placeholder)
          && String(placeholder?.key || '').trim() === template.systemOutput
      ) ||
      null;
    if (existing) consumed.add(existing);
    systemEntries.push(buildSystemEntry(template, existing));
  }
  const userPlaceholders = normalized.filter((placeholder) => !consumed.has(placeholder));

  return [...systemEntries, ...userPlaceholders];
};

const getDefaultValidation = () => ({
  enabled: false,
  type: 'llm',
  completionCriteria: '',
  validationCode: '',
  validationModel: null,
  maxRetries: 3,
  onFail: 'retry',
});

const initData = () => ({
  agentId: null,
  message: '',
  imageContext: [],
  outputPlaceholders: getDefaultOutputPlaceholders(),
  sessionMode: 'new',
  chatSessionId: '',
  validation: getDefaultValidation(),
});

const onLoadData = (rawData) => {
  // v1 compat: if fields are nested under aiAgentData, flatten them
  const flat = rawData.aiAgentData
    ? { ...rawData, ...rawData.aiAgentData }
    : { ...rawData };
  delete flat.aiAgentData;

  const data = JSON.parse(JSON.stringify({ ...initData(), ...flat }));
  data.outputPlaceholders = ensureDefaultOutputPlaceholders(
    data.outputPlaceholders
  );
  data.validation = { ...getDefaultValidation(), ...(data.validation || {}) };
  return data;
};

const { getStore: getAIAgentStore, defaultStore } = createComponentStore({
  type: 'ai_agent',
  endpoint: 'ai_agent',
  registryKey: 'ai_agent',
  initData,
  onLoadData,

  extraActions: (set, get) => ({
    setAgentId: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.agentId = value;
      set({ data });
    },

    setMessage: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.message = value;
      set({ data });
    },

    setImageContext: (imageUrl) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.imageContext) data.imageContext = [];
      if (data.imageContext.includes(imageUrl)) return;
      data.imageContext.push(imageUrl);
      set({ data });
    },

    deleteImageChip: (index) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.imageContext) return;
      data.imageContext.splice(index, 1);
      set({ data });
    },

    setSessionMode: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.sessionMode = value;
      set({ data });
    },

    setChatSessionId: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.chatSessionId = value;
      set({ data });
    },

    setOutputPlaceholders: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.outputPlaceholders = ensureDefaultOutputPlaceholders(value);
      set({ data });
    },

    addOutputPlaceholder: () => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.outputPlaceholders) data.outputPlaceholders = [];
      data.outputPlaceholders.push({ key: '', type: 'string', desc: '' });
      set({ data });
    },

    removeOutputPlaceholder: (index) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (data.outputPlaceholders) {
        const targetPlaceholder = data.outputPlaceholders[index];
        if (isDefaultOutputPlaceholder(targetPlaceholder)) return;
        data.outputPlaceholders.splice(index, 1);
        data.outputPlaceholders =
          ensureDefaultOutputPlaceholders(data.outputPlaceholders);
        set({ data });
      }
    },

    updateOutputPlaceholder: (index, field, value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (data.outputPlaceholders) {
        data.outputPlaceholders[index][field] = value;
        set({ data });
      }
    },

    setValidationField: (field, value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.validation) data.validation = getDefaultValidation();
      data.validation[field] = value;
      set({ data });
    },

    setValidationEnabled: (enabled) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.validation) data.validation = getDefaultValidation();
      data.validation.enabled = enabled;
      set({ data });
    },
  }),
});

export { getAIAgentStore, isDefaultOutputPlaceholder, isSystemOutputPlaceholder };
export default defaultStore;
